import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import { nanoid } from 'nanoid';
import { categoryFor, iconFor } from '../lib/attachmentTypes';
import { openAttachmentPreview } from '../lib/attachmentPreview';

export function mintAttachmentChipId(): string {
  return nanoid(10);
}

export const AttachmentChip = Node.create({
  name: 'attachmentChip',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      id: {
        default: '',
        parseHTML: (el: HTMLElement) => el.getAttribute('id') ?? '',
        renderHTML: (attrs: Record<string, any>) => (attrs.id ? { id: attrs.id } : {}),
      },
      attachmentId: {
        default: '',
        parseHTML: (el: HTMLElement) => el.getAttribute('attachmentId') ?? '',
        renderHTML: (attrs: Record<string, any>) => ({ attachmentId: attrs.attachmentId }),
      },
      filename: {
        default: '',
        parseHTML: (el: HTMLElement) => el.getAttribute('filename') ?? '',
        renderHTML: (attrs: Record<string, any>) => ({ filename: attrs.filename }),
      },
      mimetype: {
        default: '',
        parseHTML: (el: HTMLElement) => el.getAttribute('mimetype') ?? '',
        renderHTML: (attrs: Record<string, any>) => ({ mimetype: attrs.mimetype }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'attachment-chip' }];
  },

  renderHTML({ HTMLAttributes }) {
    // Zero-width space preserves the tag through Turndown (blank-element
    // suppression would otherwise drop it). Invisible in the editor because
    // the React NodeView takes over rendering.
    return [
      'attachment-chip',
      mergeAttributes(HTMLAttributes, { class: 'attachment-chip' }),
      '\u200b',
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(AttachmentChipNodeView);
  },

  addCommands() {
    return {
      insertAttachmentChip:
        (opts: { attachmentId: string; filename: string; mimetype: string; id?: string }) =>
        ({ state, dispatch }: { state: any; dispatch: any }) => {
          const id = opts.id && opts.id.length > 0 ? opts.id : mintAttachmentChipId();
          const node = state.schema.nodes.attachmentChip.create({
            id,
            attachmentId: opts.attachmentId,
            filename: opts.filename,
            mimetype: opts.mimetype,
          });
          if (dispatch) dispatch(state.tr.replaceSelectionWith(node));
          return true;
        },
    } as any;
  },
});

function AttachmentChipNodeView(props: NodeViewProps) {
  const { node } = props;
  const filename = (node.attrs.filename as string) || 'attachment';
  const mimetype = (node.attrs.mimetype as string) || '';
  const attachmentId = (node.attrs.attachmentId as string) || '';
  const category = categoryFor(mimetype, filename);
  const Icon = iconFor(category);

  function handleClick(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (attachmentId) openAttachmentPreview(attachmentId);
  }

  return (
    <NodeViewWrapper
      as="span"
      className="attachment-chip"
      contentEditable={false}
      draggable={false}
      onClick={handleClick}
      title={`Open ${filename}`}
    >
      <span className="inline-flex flex-col items-center justify-center gap-1 align-middle px-3 py-2 mx-1 rounded border border-ink-700 bg-ink-900 hover:bg-ink-800 cursor-pointer">
        <Icon size={22} className="text-dragon-500" />
        <span className="text-[11px] text-ink-200 leading-tight max-w-[140px] break-all text-center">
          {filename}
        </span>
      </span>
    </NodeViewWrapper>
  );
}
